import { useState, useRef, useEffect } from "react";
import { toInputDate, toDisplayDate } from "../utils/format";

/**
 * 읽기 시작일 / 완료일 입력 필드.
 *
 * 예전 구현이 자연스럽지 않았던 이유 세 가지를 여기서 정리한다.
 *
 *  1. onBlur 에 편집 종료가 걸려 있어, 네이티브 달력이 포커스를 가져가는 순간
 *     입력창이 사라졌다. 브라우저에 따라 달력이 열리자마자 닫혔다.
 *     -> blur 종료를 한 박자 늦춰(120ms) 달력이 포커스를 가져가는 것과 구분한다.
 *  2. onChange 가 타이핑 도중에도 발생하는데 빈 값을 그대로 저장해서,
 *     연도를 고쳐 쓰는 것만으로 완료일이 지워질 수 있었다.
 *     -> 빈 값은 저장하지 않는다. 지우기는 명시적 버튼으로만 한다.
 *  3. 날짜를 고르면 바로 닫혀서 다시 고르거나 취소할 수 없었다.
 *     -> 고른 뒤에도 열어 두고, Esc 로 취소한다.
 *  4. onChange 에서 곧바로 저장하고 닫았다. 그런데 네이티브 달력은 연·월·일
 *     휠을 돌릴 때마다 change 를 낸다. 연도만 바꿔도 날짜가 완성된 것으로
 *     보고 저장·종료되어, 일자까지 고를 수가 없었다.
 *     -> 고르는 것과 저장하는 것을 나눈다. change 는 값만 담아두고,
 *        '저장' 이나 Enter 로만 확정한다.
 */
const DateField = ({
  label,
  value,          // "2024.01.01" 표시 형식
  canEdit,
  onSave,         // (displayStr) => Promise
  onClear,        // 있으면 '지우기' 버튼을 보여준다
  min,            // "2024.01.01" — 이 날짜보다 앞서면 거부
  emptyText = "—",
  completed = false,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const inputRef = useRef(null);
  const closeTimer = useRef(null);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  // 편집을 시작하면 달력을 바로 띄운다. 예전에는 입력창 안의 작은 아이콘을
  // 정확히 눌러야 열려서, 날짜 글자를 눌렀는데 아무 일도 안 일어나는 것처럼 보였다.
  useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    try {
      el.showPicker?.();
    } catch {
      // showPicker 를 막는 브라우저에서는 포커스만 준다
    }
  }, [editing]);

  const open = () => {
    setDraft(toInputDate(value) || "");
    setError("");
    setEditing(true);
  };

  const close = () => {
    clearTimeout(closeTimer.current);
    setEditing(false);
    setError("");
  };

  /** 저장할 수 있는 값인지. 이유가 있으면 문구를, 없으면 빈 문자열을 준다. */
  const reasonInvalid = (val) => {
    if (!val) return "날짜를 골라주세요";
    // 연도 칸에 20 을 치면 0020 년이 그대로 저장됐다. 실제로 한 건 들어있다.
    if (val < MIN_DATE) return "연도를 네 자리로 입력해주세요";
    if (val > todayInput()) return "오늘 이후 날짜는 넣을 수 없어요";
    if (min && val < toInputDate(min)) return "시작일보다 앞설 수 없어요";
    return "";
  };

  // 값만 담아둔다. 여기서 저장하면 연도 휠을 돌리는 순간 닫혀버린다.
  const handleChange = (e) => {
    const val = e.target.value;
    setDraft(val);
    setError(val ? reasonInvalid(val) : "");
  };

  const dirty = draft && draft !== toInputDate(value);

  const commit = async () => {
    const why = reasonInvalid(draft);
    if (why) { setError(why); return; }
    setSaving(true);
    try {
      await onSave(toDisplayDate(draft));
      close();
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") { e.preventDefault(); close(); }
    if (e.key === "Enter") { e.preventDefault(); commit(); }
  };

  // 달력이 포커스를 가져가는 것과 진짜 이탈을 구분하기 위해 종료를 늦춘다.
  // 고른 값이 남아 있으면 닫지 않는다 — 닫으면 고른 게 조용히 사라진다.
  const handleBlur = () => {
    closeTimer.current = setTimeout(() => {
      if (!dirty) setEditing(false);
    }, 120);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);

  if (editing && canEdit) {
    return (
      <div className="book-date-item">
        <span className="book-date-label">{label}</span>
        <input
          ref={inputRef}
          type="date"
          className="book-date-input"
          value={draft}
          min={min ? toInputDate(min) : MIN_DATE}
          max={todayInput()}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          onFocus={cancelClose}
        />
        <div className="book-date-actions">
          <button
            type="button"
            className="book-date-action save"
            onMouseDown={cancelClose}
            onClick={commit}
            disabled={!dirty || saving || !!error}
          >
            {saving ? "저장 중…" : "저장"}
          </button>
          {onClear && value && (
            <button
              type="button"
              className="book-date-action"
              onMouseDown={cancelClose}
              onClick={async () => { await onClear(); close(); }}
            >
              지우기
            </button>
          )}
          <button
            type="button"
            className="book-date-action"
            onMouseDown={cancelClose}
            onClick={close}
          >
            취소
          </button>
        </div>
        {error && <div className="book-date-error">{error}</div>}
      </div>
    );
  }

  return (
    <div className="book-date-item">
      <span className="book-date-label">{label}</span>
      <span
        className={`book-date-value${completed ? " completed" : ""}${canEdit ? " editable" : ""}`}
        onClick={() => canEdit && open()}
        role={canEdit ? "button" : undefined}
        tabIndex={canEdit ? 0 : undefined}
        onKeyDown={(e) => {
          if (!canEdit) return;
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(); }
        }}
      >
        {value || emptyText}
      </span>
    </div>
  );
};

/** 이보다 앞선 날짜는 오타로 본다. 종이책이 이보다 오래됐을 수는 있어도
 *  MUROO 에 기록할 독서 이력은 아니다. */
const MIN_DATE = "1900-01-01";

/** 오늘 (YYYY-MM-DD). 한국 기준 — UTC 로 자르면 새벽에 어제가 나온다. */
const todayInput = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Seoul",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).format(new Date());

export default DateField;
